import React, { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import API_BASE_URL from "../../../../Constants";
import ViewModalHeader from "../../../../components/Modals/ModalControls/ModalHeader"; // Adjust the path as needed
import { RenderFields } from "../../../setup/components/Customer/RenderFields";
import AgencyAccordion from "./AgencyAccordion"; // Adjust the path as needed
import {
  agencyGeneralFields,
  testDetailsFields, // Ensure this is imported
} from "./config";

import AgencyTestTable from "./AgencyTestTable";
import { useAuth } from "../../../../contexts/AuthContext";


const ViewAgencyModal = ({
  open,
  handleClose,
  selectedAgency,
  agency: propAgency, // Rename to avoid confusion
  setAgency,
  setNext,
  setPrev,
  allAgencies,
  updateAll,
  navHeight, // Add navHeight as a prop
}) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const { user } = useAuth();
  const [pendingRequest, setPendingRequest] = useState("");
  const [lockedModal, setLockedModal] = useState(false);
  const [lockOwner, setLockOwner] = useState("");
  const [eventSRC, setEventSRC] = useState(null);
  const [subClassOptionsKey, setSubClassOptionsKey] = useState(null);
  const [selectedTest, setSelectedTest] = useState(null); // State for selected test
  const [showTestDetails, setShowTestDetails] = useState(false); // Add this state

  // Use `propAgency` or fallback to `selectedAgency`
  const agency = propAgency || selectedAgency;

  // Handle modal close
  const handleCloseModal = () => {
    handleClose(); // Call the parent's handleClose function
    setSelectedTest(null); // Clear the selected test
    setShowTestDetails(false); // Hide the test details
  };

  // Clear user locks
  const clearUserLocks = async (username) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/record/${encodeURIComponent(
          username
        )}/clear-locks`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        }
      );

      if (response.ok) {
        const message = await response.text();
        console.log("Server response:", message);
        notifyRequesters(agency.agencyId); // Notify other users that the lock has been cleared
      } else {
        throw new Error("Failed to clear locks");
      }
    } catch (error) {
      console.error("An error occurred:", error.message);
    }
  };

  // Notify requesters
  const notifyRequesters = async (recordId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/notify/requesters?recordId=${encodeURIComponent(
          recordId + "-agency"
        )}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        }
      );

      if (response.ok) {
        const message = await response.text();
        console.log("Server response:", message);
      } else {
        throw new Error("Failed to notify requesters");
      }
    } catch (error) {
      console.error("An error occurred:", error.message);
    }
  };

  useEffect(() => {
    // If dispatchClass changed, reset dispatchSubClass to null
    if (agency && Number(agency.dispatchClass) !== subClassOptionsKey) {
      setSubClassOptionsKey(Number(agency.dispatchClass));
      setAgency({ ...agency, dispatchSubClass: null });
    }
  }, [agency, subClassOptionsKey, setAgency]);

  useEffect(() => {
    // Turn off edit mode on open
    setIsEditMode(false);
  }, [open]);

  useEffect(() => {
    // Open SSE connection for the user
    if (user && user.username) {
      const eventSource = new EventSource(
        `${API_BASE_URL}/sse/subscribe?username=${encodeURIComponent(
          user.username
        )}`
      );
      setEventSRC(eventSource);

      eventSource.onmessage = (event) => {
        alert(event.data);
      };

      eventSource.onerror = (err) => {
        console.error("EventSource failed:", err);
        eventSource.close();
      };

      return () => {
        eventSource.close();
      };
    }
  }, [user]);

  useEffect(() => {
    return () => {
      if (eventSRC) {
        eventSRC.close();
      }
    };
  }, [eventSRC]);

  const handleSaveChanges = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/inspection/agency/agencies/${agency.agencyId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(agency),
        }
      );
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const data = await response.json();

      // Update the agency in the list
      updateAll(
        allAgencies.map((a) => (a.agencyId === agency.agencyId ? data : a))
      );

      // Clear user locks and exit edit mode
      clearUserLocks(user.username);
      setIsEditMode(false);
    } catch (error) {
      console.error("Fetch error: ", error);
    }
  };

  const handleEditModal = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/record/${agency.agencyId
        }-agency/lock?user=${encodeURIComponent(user.username)}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        }
      );

      if (response.ok) {
        // If the record is successfully locked, toggle edit mode
        setIsEditMode((prevMode) => !prevMode);
      } else if (response.status === 401) {
        // If the record is locked by another user, show the locked modal
        const owner = await response.text();
        setLockOwner(owner);
        setPendingRequest(agency.agencyId + "-agency");
        setLockedModal(true);
      } else {
        throw new Error("Failed to lock record");
      }
    } catch (error) {
      alert("An error occurred: " + error.message);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/inspection/agency/deleteAgency/${agency.agencyId}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );
      if (!response.ok) {
        throw new Error("Failed to delete agency");
      }

      // Remove the agency from the list
      updateAll(allAgencies.filter((a) => a.agencyId !== agency.agencyId));

      // Clear user locks and close the modal
      clearUserLocks(user.username);
      handleClose();
    } catch (error) {
      console.error("Fetch error: ", error);
    }
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setAgency((prev) => ({ ...prev, [name]: value }));
  };

  const splitFields = (fieldsArray) => {
    const midIndex = Math.ceil(fieldsArray.length / 2);
    return [fieldsArray.slice(0, midIndex), fieldsArray.slice(midIndex)];
  };

  // Handle test row click
  const handleTestRowClick = (test) => {
    setSelectedTest(test);
    setShowTestDetails(true); // Show the test details
  };

  // If no agency is selected, render a placeholder or loading state
  if (!agency) {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        fullWidth
        PaperProps={{
          style: {
            position: "fixed", // Use fixed positioning
            top: navHeight + 50, // Add padding below the navbar
            left: "50%",
            transform: "translateX(-50%)",
            margin: 4,
            transition: "width 0.3s ease, height 0.3s ease",
            width: isExpanded ? "90%" : "1000px",
            overflow: "auto",
          },
        }}
        style={{ zIndex: 1100 }} // Also set zIndex here for the Dialog
      >
        <DialogContent>
          <p>No agency selected.</p>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      {open && (
        <>
          {/* Navigation arrows for previous/next record */}
          <button
            onClick={setPrev}
            disabled={isEditMode}
            style={{
              position: "fixed",
              left: `calc(50% - ${isExpanded ? "45%" : "500px"} - 60px)`,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 1500,
            }}
            className={`p-2 rounded-full bg-white hover:bg-gray-200 transition ${isEditMode ? "opacity-50 cursor-not-allowed" : ""
              }`}
            aria-label="Previous Agency"
          >
            <ArrowBackIcon />
          </button>
          <button
            onClick={setNext}
            disabled={isEditMode}
            style={{
              position: "fixed",
              right: `calc(50% - ${isExpanded ? "45%" : "500px"} - 60px)`,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 1500,
            }}
            className={`p-2 rounded-full bg-white hover:bg-gray-200 transition ${isEditMode ? "opacity-50 cursor-not-allowed" : ""
              }`}
            aria-label="Next Agency"
          >
            <ArrowForwardIcon />
          </button>
        </>
      )}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        PaperProps={{
          style: {
            width: isExpanded ? "90%" : "1000px",
            maxHeight: "75vh",
            minHeight: "70vh",
          },
        }}
        fullWidth
        style={{ zIndex: 1400 }}
      >
        <ViewModalHeader
          isExpanded={isExpanded}
          setIsExpanded={setIsExpanded}
          handleCloseModal={handleCloseModal}
          isEditMode={isEditMode}
          handleEditButtonClick={handleEditModal}
          handleCancel={() => setIsEditMode(false)}
          handleDelete={handleDelete}
          isSaving={false} // You can add a state for this if needed
          autosaveStatus={""} // You can add a state for this if needed
          handleOpenAddModal={() => { }} // You can add a function for this if needed
        />

        <DialogContent
          dividers
          sx={{
            padding: 0,
            maxHeight: "90vh",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div className="p-4 space-y-5 rounded-2xl">
            {/* Render your sections (General Info, Address Info, etc.) using agency */}
            {(() => {
              const [generalInfoLeft, generalInfoRight] =
                splitFields(agencyGeneralFields);
              return (
                <AgencyAccordion label="General Info">
                  <div
                    className={`flex ${isExpanded ? "flex-row" : "flex-col md:flex-row"
                      } gap-4`}
                  >
                    <div className="w-full">
                      <RenderFields
                        fields={generalInfoLeft}
                        formData={agency}
                        handleChange={handleFieldChange}
                        readOnly={!isEditMode}
                      />
                    </div>
                    <div className="w-full">
                      <RenderFields
                        fields={generalInfoRight}
                        formData={agency}
                        handleChange={handleFieldChange}
                        readOnly={!isEditMode}
                      />
                    </div>
                  </div>
                </AgencyAccordion>
              );
            })()}

            {/* Test Table and Test Details Card */}
            <AgencyAccordion label="Test Table">
              <div className="flex flex-row gap-4">
                {/* Test Table (Left Side) */}
                <div className={`${showTestDetails ? "w-1/2" : "w-full"}`}>
                  <AgencyTestTable
                    agency={agency}
                    isEditMode={isEditMode}
                    onRowClick={handleTestRowClick}
                  />
                </div>

                {/* Test Details Card (Right Side) */}
                {showTestDetails && selectedTest && (
                  <div className="w-1/2">
                    <div className="bg-gray-50 p-4 rounded-lg shadow-sm">
                      <h2 className="text-xl font-bold mb-4">Test Details</h2>
                      <RenderFields
                        fields={testDetailsFields}
                        formData={selectedTest}
                        handleChange={(e) => {
                          const { name, value } = e.target;
                          setSelectedTest((prev) => ({
                            ...prev,
                            [name]: value,
                          }));
                        }}
                        readOnly={!isEditMode}
                      />
                      <div className="flex justify-end mt-4">
                        <button
                          onClick={() => setShowTestDetails(false)}
                          className="px-4 py-2 bg-gray-300 text-gray-800 font-bold rounded-md hover:bg-gray-400 transition"
                        >
                          Close
                        </button>
                        {isEditMode && (
                          <button
                            onClick={() => {
                              // Handle save logic here
                              console.log("Updated Test:", selectedTest);
                            }}
                            className="px-6 py-2 bg-yellow-500 text-gray-800 font-bold rounded-md hover:bg-yellow-600 transition ml-2"
                          >
                            Save
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </AgencyAccordion>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ViewAgencyModal;
